'use client';
import styled from 'styled-components';
import { Image } from '@/shared/ui/image';
import { BodyTextRegularB, BodyTextRegularC } from '@/shared/ui/typography2';
import { useScopedI18n } from '@locales/client';
import { useEffect, useState } from 'react';

export const AppsBlock = () => {
  const t = useScopedI18n('common.footer');
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isSafari, setIsSafari] = useState(false);

  useEffect(() => {
    window.addEventListener('beforeinstallprompt', (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    });

    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    setIsSafari(isSafari);
  }, []);

  const handleInstallClick = () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      deferredPrompt.userChoice.then((choiceResult) => {
        if (choiceResult.outcome === 'accepted') {
          console.log('User accepted the install prompt');
        } else {
          console.log('User dismissed the install prompt');
        }
        setDeferredPrompt(null);
      });
    }
  };

  return (
    <AppsBlockWrapper>
      <AppsButton onClick={handleInstallClick} disabled={!deferredPrompt}>
        <Image src="/images/icons/footer/app-apple.svg" alt="Apple app" />
        <TextBlock>
          <BodyTextRegularC color="#AFAFAF">
            {t('application')}
          </BodyTextRegularC>
          <BodyTextRegularB color="#FFF">
            {t('application_target', { target: 'iOS' })}
          </BodyTextRegularB>
        </TextBlock>
      </AppsButton>
      {!isSafari && (
        <AppsButton onClick={handleInstallClick} disabled={!deferredPrompt}>
          <Image src="/images/icons/footer/app-android.svg" alt="Android app" />
          <TextBlock>
            <BodyTextRegularC color="#AFAFAF">
              {t('application')}
            </BodyTextRegularC>
            <BodyTextRegularB color="#FFF">
              {t('application_target', { target: 'Android' })}
            </BodyTextRegularB>
          </TextBlock>
        </AppsButton>
      )}
      <WindowsButton
        $hideOnMobile={true}
        onClick={handleInstallClick}
        disabled={!deferredPrompt}
      >
        <Image src="/images/icons/footer/app-windows.svg" alt="Android app" />
        <TextBlock>
          <BodyTextRegularC color="#AFAFAF">
            {t('application')}
          </BodyTextRegularC>
          <BodyTextRegularB color="#FFF">
            {t('application_target', { target: 'Windows' })}
          </BodyTextRegularB>
        </TextBlock>
      </WindowsButton>
    </AppsBlockWrapper>
  );
};

const AppsBlockWrapper = styled.section`
  display: flex;
  justify-content: center;
  width: 100%;
  gap: 12px;

  @media screen and (min-width: 641px) and (max-width: 840px) {
    flex-direction: column;
  }
  @media screen and (min-width: 1280px) {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    grid-template-rows: repeat(2, 1fr);
    grid-auto-flow: column;
    gap: 18px;
  }
`;
const AppsButton = styled.button<{ $hideOnMobile?: boolean }>`
  display: flex;
  position: relative;
  min-width: 136px;
  max-width: 224px;
  height: 50px;
  padding: 10px;
  align-items: center;
  box-sizing: border-box;
  gap: 6px;
  flex: 1 0 0;
  cursor: pointer;

  border-radius: 8px;
  background: #27262b;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.8;
    pointer-events: none;

    &::after {
      content: '';
      position: absolute;
      inset: 0;
      background-color: rgba(0, 0, 0, 0.6);
      border-radius: 8px;
      z-index: 1;
    }
  }

  @media screen and (min-width: 640px) {
    height: 60px;
  }
  @media screen and (min-width: 840px) {
    height: 64px;
  }

  ${({ $hideOnMobile }) =>
    $hideOnMobile &&
    `@media screen and (max-width: 840px) {
        display: none !important;
    }
`}
`;
const WindowsButton = styled(AppsButton)`
  @media screen and (min-width: 1280px) {
    flex-direction: column;
    justify-content: space-between;
    align-items: flex-start;
    grid-row: 1 / -1;
    height: 100%;
  }
`;

const TextBlock = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
`;
